import { PushHandler } from './push-handler';
import { BehaviorSubject, from, NEVER, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { initializeApp } from 'firebase/app';
import { deleteToken, getMessaging, getToken, isSupported } from 'firebase/messaging';
import 'firebase/messaging';
import { catchError, map, startWith, switchMap, tap } from 'rxjs/operators';
import { PushData } from './push-data';
import { ILogger } from '../logger/logger.service';
import { Messaging } from '@firebase/messaging';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';

export class WebPushHandlerService implements PushHandler {
  supported$ = new BehaviorSubject<boolean>(false);
  permission$ = new BehaviorSubject<NotificationPermission>(window['Notification'] === undefined ? 'default' : Notification.permission);
  permissionGranted$ = this.permission$.pipe(map((permission) => permission === 'granted'));
  tokenChanges$ = new Subject<string | null>();
  foregroundMessage$ = new ReplaySubject<PushData>(1);
  private swRegistration?: ServiceWorkerRegistration;
  private firebaseMessaging: Messaging;
  private firebaseProjectId: string;

  constructor(private logger: ILogger) {}

  getFirebaseProjectId(): string {
    return this.firebaseProjectId;
  }

  init(config: any): Observable<void> {
    console.log('init web push service');
    this.firebaseProjectId = config.projectId;

    const app = initializeApp(config);

    return from(isSupported()).pipe(
      switchMap((isSupported) => {
        if (isSupported) {
          this.firebaseMessaging = getMessaging(app);
          navigator.serviceWorker.addEventListener('message', (event) => {
            this.logger.log('received message from SW in handler', event);
            if (
              event.data &&
              event.data.firebaseMessagingType === 'push-msg-received' &&
              event.data.firebaseMessagingData &&
              event.data.firebaseMessagingData.data &&
              event.data.firebaseMessagingData.data.appRoute
            ) {
              this.foregroundMessage$.next(event.data.firebaseMessagingData.data);
            } else if (
              event.data &&
              event.data.firebaseMessaging &&
              event.data.firebaseMessaging.type === 'push-received' &&
              event.data.firebaseMessaging.payload &&
              event.data.firebaseMessaging.payload.data &&
              event.data.firebaseMessaging.payload.data.appRoute
            ) {
              this.foregroundMessage$.next(event.data.firebaseMessaging.payload.data);
            } else if (
              event.data &&
              event.data.type === 'PUSH' &&
              event.data.data &&
              event.data.data.data &&
              event.data.data.data.appRoute
            ) {
              this.foregroundMessage$.next(event.data.data.data);
            }
          });

          return from(
            navigator.serviceWorker
              .getRegistration()
              .then((registration) => {
                if (!registration) {
                  return navigator.serviceWorker.register(new URL('firebase-messaging-sw.js', document.baseURI).toString());
                }

                return registration;
              })
              .then((registration) => {
                if (!registration) {
                  throw new Error('Failed to obtain service worker registration');
                }

                // Verify pushManager is available
                if (!registration.pushManager) {
                  throw new Error('Push messaging is not supported in this browser');
                }

                return navigator.serviceWorker.ready;
              })
              .then((registration) => {
                this.swRegistration = registration;
                this.supported$.next(true);
              })
              .catch((error) => {
                this.logger.error('Service worker registration failed:', error);

                this.supported$.next(false);

                throw error;
              }),
          );
        } else {
          return NEVER;
        }
      }),
    );
  }

  register(): Observable<string> {
    return fromPromise(getToken(this.firebaseMessaging, { serviceWorkerRegistration: this.swRegistration }));
  }

  requestPermission(): Observable<NotificationPermission> {
    if (window['Notification'] === undefined) {
      this.permission$.next('default');
      return of('default');
    } else {
      return from(Notification.requestPermission()).pipe(
        tap((permission) => {
          this.permission$.next(permission);
        }),
      );
    }
  }

  unregister(): Observable<void> {
    return fromPromise(deleteToken(this.firebaseMessaging)).pipe(map(() => null));
  }

  /**
   * WebPush does not support app icon badges, yet => to nothing
   */
  clearApplicationIconBadge(): Observable<void> {
    return of(null);
  }
}
